import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {
  AddTriggerConfigurationRequest,
  TriggerConfiguration
} from '@models/adminstration/machine/trigger/trigger-configuration';
import {TriggerQuery} from '@models/adminstration/machine/trigger/trigger-query/trigger-query';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {ExpressionSignal} from '@models/adminstration/machine/trigger/expression-signal';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {TRIGGER_ACTION_TYPE} from '@constants/trigger/trigger-action-type.constant';
import {TriggerExpression} from '@models/adminstration/machine/trigger/trigger-expression';
import {TriggerExpressionTypes} from '@constants/administration/machine/trigger-expression-type';
import {TRIGGER_TYPE} from '@constants/administration/machine/trigger-type';
import {TriggerExpressionOperators} from '@constants/administration/machine/trigger-expression-operators';
import {TriggerTimeCondition} from '@models/adminstration/machine/trigger/trigger-time-condition';
import {Role} from '@models/user/role';
import {SignalEdge} from '@models/adminstration/machine/trigger/signal-edge';

@Injectable({
  providedIn: 'root'
})
export class TriggerConfigurationService {

  private TRIGGER_CONFIGURATION_LIST_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration';
  private TRIGGER_CONFIGURATION_GAP_DETECTION_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration/gapDetection';
  private TRIGGER_CONFIGURATION_DELETE_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration/:config';
  private TRIGGER_CONFIGURATION_ACTIVATE_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration/activateTriggerConfig/:config';
  private TRIGGER_CONFIGURATION_DEACTIVATE_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration/deactivateTriggerConfig/:config';
  private TRIGGER_CONFIGURATION_FORCE_TRIGGER_EXECUTION_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration/forceTriggerExecution/:config';
  private TRIGGER_CONFIGURATION_ADD_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration';
  private TRIGGER_CONFIGURATION_UPDATE_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration?configId=:config';
  private TRIGGER_CONFIGURATION_LIST_AVAILABLE_TRIGGER_QUERY_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration/listAvailableQueries?externalTriggerApi=false';
  private TRIGGER_CONFIGURATION_IS_TRIGGER_ACTIVE_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration/triggerIsActiveOrPaused/:config';
  private TRIGGER_CONFIGURATION_LIST_CLOUD_PLUGS_URL_SUFFIX = '/api/web/admin/machine/:id/listGateways';
  private TRIGGER_CONFIGURATION_LIST_TRIGGER_EXPRESSION_SIGNALS_URL_SUFFIX = '/api/web/admin/machine/:id/triggerConfiguration/triggerExpressionSignals';

  private readonly BASE_URL: string;

  private readonly signalsLoadingSubject = new BehaviorSubject(false);

  constructor(
      private http: HttpClient,
      private apiLocation: ApiLocationService
  ) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  getSignalsLoadingSubject() {
    return this.signalsLoadingSubject;
  }

  private static findSignalByNameAndTable(signalName: string | undefined, signalTable: string | undefined, signals: ExpressionSignal[]) {
    if (!signals || signals.length === 0 || !signalName || !signalTable) {
      return null;
    }

    for (const signal of signals) {
      if (signal.signalColumn === signalName && signal.signalTable == signalTable) {
        return signal;
      }
    }
    return null;
  }

  public triggerConfiguration(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_LIST_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get<TriggerConfiguration[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public gapDetection(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_GAP_DETECTION_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get<number[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public delete(machineId: number, triggerConfigurationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_DELETE_URL_SUFFIX;
    url = url.replace(':id', String(machineId)).replace(':config', String(triggerConfigurationId));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public activateTriggerConfig(parentId: number, triggerConfigurationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_ACTIVATE_URL_SUFFIX;
    url = url.replace(':id', String(parentId)).replace(':config', String(triggerConfigurationId));
    const request = {
      config: triggerConfigurationId,
      id: parentId
    };
    return this.http.post<TriggerConfiguration>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public deactivateTriggerConfig(parentId: number, triggerConfigurationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_DEACTIVATE_URL_SUFFIX;
    url = url.replace(':id', String(parentId)).replace(':config', String(triggerConfigurationId));
    const request = {
      config: triggerConfigurationId,
      id: parentId
    };
    return this.http.post<TriggerConfiguration>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public forceTriggerExecution(parentId: number, triggerConfigurationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_FORCE_TRIGGER_EXECUTION_URL_SUFFIX;
    url = url.replace(':id', String(parentId)).replace(':config', String(triggerConfigurationId));
    const request = {
      config: triggerConfigurationId,
      id: parentId
    };
    return this.http.post<TriggerConfiguration>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public addTriggerConfiguration(machineId: number, request: AddTriggerConfigurationRequest): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_ADD_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.post<TriggerConfiguration>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public updateTriggerConfiguration(machineId: number, request: TriggerConfiguration): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_UPDATE_URL_SUFFIX;
    url = url.replace(':id', String(machineId)).replace(':config', String(request.id));
    return this.http.post<TriggerConfiguration>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public getAvailableTriggerQueries(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_LIST_AVAILABLE_TRIGGER_QUERY_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get<TriggerQuery[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public triggerIsActive(machineId: number, triggerConfigurationId: number): Observable<any> {
    if (triggerConfigurationId <= 0) {
      return of(false);
    }
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_IS_TRIGGER_ACTIVE_URL_SUFFIX;
    url = url.replace(':id', String(machineId)).replace(':config', String(triggerConfigurationId));
    return this.http.get<boolean>(url, HTTPS_REQUEST_OPTIONS);
  }

  public getCloudPlugs(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_LIST_CLOUD_PLUGS_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get<Gateway[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public getTriggerExpressionSignals(gatewayId: string): Observable<any> {
    let url: string = this.BASE_URL + this.TRIGGER_CONFIGURATION_LIST_TRIGGER_EXPRESSION_SIGNALS_URL_SUFFIX;
    url = url.replace(':id', gatewayId);
    return this.http.get<ExpressionSignal[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public triggerActionRemoveButtonDisabled(userRole: Role, triggerActionType: string): boolean {
    const isNotificationOrEmail = triggerActionType === TRIGGER_ACTION_TYPE.EMAIL || triggerActionType === TRIGGER_ACTION_TYPE.NOTIFICATION;
    if (!userRole.isSuperAdminRole && triggerActionType === TRIGGER_ACTION_TYPE.PUBSUB) {
      return true;
    }
    if (userRole.isSuperAdminRole || userRole.isTechAdminRole || userRole.isAdminRole) {
      return false;
    }

    if (userRole.isDistributorRole && isNotificationOrEmail) {
      return false;
    }

    return !((userRole.isUserRole || userRole.isAnalyticsRole) && (triggerActionType === TRIGGER_ACTION_TYPE.EMAIL));
  }

  public setTriggerExpressionsRequest(triggerExpressions: TriggerExpression[]) {
    const fieldsToDelete = {
      [TriggerExpressionTypes.TIME_EVAL]: [
        'arithmeticOperator',
        'signalLabel',
        'signalUnit',
        'triggerColumnName',
        'triggerCorrelation',
        'triggerLastValue',
        'triggerTableName',
        'triggerPrecedentValue'
      ],
      [TriggerExpressionTypes.VALUE_CHANGE]: [
        'arithmeticOperator',
        'triggerLastValue',
        'triggerPrecedentValue',
        'triggerTimeCondition'
      ],
      [TriggerExpressionTypes.CONDITION]: [
        'triggerPrecedentValue',
        'triggerTimeCondition'
      ],
      [TriggerExpressionTypes.NULLEVALUATION]: [
        'triggerLastValue',
        'triggerPrecedentValue',
        'triggerTimeCondition'
      ]
    };
    for (const expression of triggerExpressions) {
      const fields = fieldsToDelete[expression.triggerExpressionType];
      if (fields) {
        for (const field of fields) {
          delete expression[field];
        }
      }
    }
    return triggerExpressions;
  }

  public setExpressionsLabelsAndUnits(triggerExpressions: TriggerExpression[], signals: ExpressionSignal[]) {
    if (triggerExpressions.length > 0) {
      triggerExpressions.forEach(expression => {
        const signal = TriggerConfigurationService.findSignalByNameAndTable(expression.triggerColumnName, expression.triggerTableName, signals);
        if (signal) {
          expression.signalLabel = signal.signalLabel;
          expression.signalUnit = signal.signalUnit;
        }
      });
    }
  }

  public isDeleteTriggerConfigurationButtonDisabled(isAdminRole: boolean, isTechAdminRole: boolean, isSuperAdminRole: boolean,
                                                    triggerConfiguration: TriggerConfiguration, isGapDetectionInProgress: any): boolean {
    return triggerConfiguration.isActive || isGapDetectionInProgress[triggerConfiguration.id] ||
        !(isSuperAdminRole || isTechAdminRole || isAdminRole);
  }

  public isActivateTriggerConfigurationButtonDisabled(isAdminRole: boolean, isTechAdminRole: boolean, isSuperAdminRole: boolean,
                                                      isTriggerActivatable: boolean | undefined): boolean {
    return !(isSuperAdminRole || isTechAdminRole || isAdminRole) || !isTriggerActivatable;
  }

  public showDeactivateTriggerButton(triggerConfiguration: TriggerConfiguration, isGapDetectionInProgress: any): boolean {
    return triggerConfiguration.isActive || isGapDetectionInProgress[triggerConfiguration.id];
  }

  public showActivateTriggerButton(triggerConfiguration: TriggerConfiguration, isGapDetectionInProgress: any): boolean {
    return !triggerConfiguration.isActive && !isGapDetectionInProgress[triggerConfiguration.id];
  }

  public resolveEmailCheckBoxChange(triggerQuery: TriggerQuery, triggerConfiguration: TriggerConfiguration) {
    if (!triggerConfiguration.resolveActions) {
      triggerConfiguration.resolveActions = [];
    }

    triggerQuery.isResolveEvent = !triggerQuery.isResolveEvent;
    if (triggerQuery.isResolveEvent) {
      if (triggerConfiguration.resolveActions.indexOf(triggerQuery.id) < 0) {
        triggerConfiguration.resolveActions.push(triggerQuery.id);
      }
    } else {
      const resolveActionIndex = triggerConfiguration.resolveActions.indexOf(triggerQuery.id);

      if (resolveActionIndex >= 0) {
        triggerConfiguration.resolveActions.splice(resolveActionIndex, 1);
      }
    }
  }

  public showResolveCheckbox(triggerQuery: TriggerQuery, triggerConfiguration: TriggerConfiguration, selectedTriggerType: string) {
    if (!triggerConfiguration) {
      return false;
    }
    if (selectedTriggerType !== TRIGGER_TYPE.VALUE_BASED) {
      return false;
    }
    if (!triggerConfiguration.signalEdge || triggerConfiguration.signalEdge === SignalEdge.BOTH) {
      return false;
    }
    return triggerQuery && triggerQuery.triggerActionType === TRIGGER_ACTION_TYPE.EMAIL;
  }

  public showResolveEmailField(triggerExecutionInDataflow: boolean | undefined, selectedTriggerType: string) {
    return !triggerExecutionInDataflow && selectedTriggerType === TRIGGER_TYPE.VALUE_BASED;
  }

  public showTimeBasedComponent(isAdminOrHigher: boolean, selectedTriggerType: string) {
    return isAdminOrHigher && ( selectedTriggerType === TRIGGER_TYPE.REAL_TIME_BASED);
  }

  public showValueBasedComponent(selectedTriggerType: string) {
    return selectedTriggerType === TRIGGER_TYPE.VALUE_BASED;
  }

  public showEventSeverityBased(selectedTriggerType: string) {
    return selectedTriggerType === TRIGGER_TYPE.EVENT_SEVERITY_BASED;
  }

  public showOpcOfflineComponent(isAdminOrHigher: boolean, selectedTriggerType: string) {
    return isAdminOrHigher && selectedTriggerType === TRIGGER_TYPE.OPC_OFFLINE;
  }

  public showOpcNoDataComponent(isAdminOrHigher: boolean, selectedTriggerType: string) {
    return isAdminOrHigher && selectedTriggerType === TRIGGER_TYPE.OPC_NO_DATA;
  }


  public isSignalBasedTrigger(selectedTriggerType: string) {
    return selectedTriggerType === TRIGGER_TYPE.VALUE_BASED;
  }

  public setResolveActionsRequest(selectedTriggerType: string, triggerConfiguration: TriggerConfiguration, addTriggerConfigurationRequest: any) {
    if ((selectedTriggerType !== TRIGGER_TYPE.VALUE_BASED) || (selectedTriggerType === TRIGGER_TYPE.VALUE_BASED && triggerConfiguration.signalEdge === SignalEdge.BOTH)) {
      addTriggerConfigurationRequest.resolveActions = triggerConfiguration.resolveActions;
    }
  }

  public setRequestForSetParameter(triggerConfiguration: TriggerConfiguration, addTriggerConfigurationRequest: any) {
    if (triggerConfiguration.triggerInterval > 0) {
      addTriggerConfigurationRequest.triggerInterval = triggerConfiguration.triggerInterval;
    }
    if (triggerConfiguration.triggerIntervalType) {
      addTriggerConfigurationRequest.triggerIntervalType = triggerConfiguration.triggerIntervalType;
    }

    if (triggerConfiguration.signalEdge) {
      addTriggerConfigurationRequest.signalEdge = triggerConfiguration.signalEdge;
    }
  }

  public getOperatorSymbol(operatorName: string | undefined | null): string {
    if (!operatorName) {
      return '';
    }
    return TriggerExpressionOperators[operatorName];
  }

  public deleteParametersOnAddTimeExpression(triggerExpression: TriggerExpression, dayOfWeek: string, dayOfMonth: string) {
    if (triggerExpression.triggerTimeCondition?.type === 'DAILY') {
      delete triggerExpression.triggerTimeCondition.dayOfMonth;
      delete triggerExpression.triggerTimeCondition.dayOfWeek;
    } else if (triggerExpression.triggerTimeCondition?.type === 'WEEKLY') {
      triggerExpression.triggerTimeCondition.dayOfWeek = dayOfWeek;
      delete triggerExpression.triggerTimeCondition.dayOfMonth;
    } else if (triggerExpression.triggerTimeCondition?.type === 'MONTHLY') {
      triggerExpression.triggerTimeCondition.dayOfMonth = dayOfMonth;
      delete triggerExpression.triggerTimeCondition.dayOfWeek;
    }
  }

  getDayOfMonthValue(timeCondition: TriggerTimeCondition | undefined) {
    if (!timeCondition) {
      return '';
    } else if (timeCondition.type === 'MONTHLY') {
      return timeCondition.dayOfMonth;
    }
    return '';
  }

  getDayOfWeekValue(timeCondition: TriggerTimeCondition | undefined, daysArray: any[]) {
    if (!timeCondition) {
      return '';
    } else if (timeCondition.type === 'WEEKLY') {
      if (timeCondition.dayOfWeek !== undefined) {
        return daysArray[timeCondition.dayOfWeek].value;
      }
    }
    return '';
  }

  public availableTriggerQueries(userRole: Role, result: any[]): any[] {
    if (userRole.isViewerRole || userRole.isAnalyticsRole || userRole.isUserRole) {
      return result.filter(e => e.triggerActionType === TRIGGER_ACTION_TYPE.EMAIL);
    }
    if (userRole.isDistributorRole) {
      return result.filter(e => e.triggerActionType === TRIGGER_ACTION_TYPE.EMAIL || e.triggerActionType === TRIGGER_ACTION_TYPE.NOTIFICATION);
    }
    return result;
  }
}
